/**
 * "A Studio workflow re-edits in the Studio" — STUDIO_DESIGN.md §6.5, §11.0 R11.
 *
 * ONE predicate, two re-edit call sites, and a third caller that is not about re-editing at all:
 * the `FILE_DOWNLOAD{source}` attribute (§8). They share `isStudioWorkflow` on purpose — a card
 * that reports itself as a Studio download and then re-opens in the merge editor would be two
 * facts that disagree, and the only way to make that impossible is to derive both from one place.
 *
 * The detection is `options.composition` being an OBJECT, which is exactly the rule both server
 * tiers use (`CompositionOptions.compositionObject`: `isJsonObject`, never `has` — `has` is true
 * for `"composition": null`). A stored `FileUpload` can hold `options` as a parsed object or,
 * from an old storage record, as a string; both are handled, and neither is guessed at.
 *
 * THE ARRIVAL HALF IS P5c.1b'S. This module leaves the user on
 * `<featurePage>?tab=studio&sid=<projectId>`; what happens next — reading `sid` through
 * `readStudioUrl`, restoring the project from `readProject(sid)`, and emitting
 * `STUDIO_OPENED{source:'reedit'}` exactly once on ARRIVAL (controller ruling 4) — belongs to the
 * Studio owner, because only it knows whether the restore succeeded. Nothing is emitted here: an
 * event fired at departure would count opens that never opened.
 */
import Router from 'next/router';

import { isValidProjectId } from '../model/composition';
import { readProject } from '../state/persistence';
import { STUDIO_TAB_ID } from '../studio-gate';

/**
 * IT1-W1 (saas #9): where the re-edit link was pressed. `chip` is /downloads' "Back to Studio" row
 * over the list; the card's own key passes nothing. The Studio reads it as `STUDIO_OPENED.source`
 * (`downloads-chip` vs `reedit`) and never writes it back.
 */
export type StudioReeditSource = 'chip';

/** The narrow shape this module reads. Structural, so a spec needs no `FileUpload`. */
export interface StudioWorkflowFacts {
    options?: unknown;
    pageData?: { path?: string } | null;
    uniqueProcessType?: string;
}

/**
 * The composition on a card, or null -- THE predicate (see the module comment). Exported for the
 * card line (`card.ts`, S-43) so "is a Studio workflow" and "what does the Studio card say" are
 * derived from one reading of the same field.
 */
export function studioCompositionOf(fileUpload: StudioWorkflowFacts | null | undefined): Record<string, unknown> | null {
    let options: unknown = fileUpload?.options;
    if (typeof options === 'string') {
        try { options = JSON.parse(options); } catch { return null; }
    }
    if (!options || typeof options !== 'object') return null;
    const composition = (options as Record<string, unknown>).composition;
    if (!composition || typeof composition !== 'object' || Array.isArray(composition)) return null;
    return composition as Record<string, unknown>;
}

function compositionOf(fileUpload: StudioWorkflowFacts | null | undefined): Record<string, unknown> | null {
    return studioCompositionOf(fileUpload);
}

export function isStudioWorkflow(fileUpload: StudioWorkflowFacts | null | undefined): boolean {
    return compositionOf(fileUpload) !== null;
}

export function studioProjectIdOf(fileUpload: StudioWorkflowFacts | null | undefined): string | null {
    const id = compositionOf(fileUpload)?.projectId;
    return typeof id === 'string' && isValidProjectId(id) ? id : null;
}

/**
 * Where Re-edit should go, or null when this is an ordinary conversion.
 *
 * Null is also the answer for a composition whose `projectId` is missing or malformed: without
 * one there is no project to restore, and dropping the user into an empty Studio would be worse
 * than the merge editor they expected. `isValidProjectId` is the same guard persistence uses, so
 * the reserved spellings (`INDEX`, `LATEST-…`) that would land on its own bookkeeping keys are
 * refused here too.
 */
export function studioReeditHref(fileUpload: StudioWorkflowFacts | null | undefined, source?: StudioReeditSource): string | null {
    const projectId = studioProjectIdOf(fileUpload);
    if (!projectId) return null;
    const slug = fileUpload?.pageData?.path || fileUpload?.uniqueProcessType;
    if (!slug) return null;
    return `/${slug}?tab=${STUDIO_TAB_ID}&sid=${encodeURIComponent(projectId)}${source ? `&src=${source}` : ''}`;
}

/**
 * IT1-W1 (saas #1): is this card's project still in THIS browser?
 *
 * /downloads says "the project is still open in the Studio" and offers "Back to Studio" / "Re-edit"
 * off the card alone; after the 2.5 h, in another browser, or after cleared storage the record is
 * gone and the link opens an empty editor. `readProject` is the Studio's own reader (it enforces the
 * TTL), so the row and the editor cannot disagree about whether there is anything to go back to.
 * `false` for a non-Studio card too -- there is no project to be open.
 */
export function studioProjectStillOpen(fileUpload: StudioWorkflowFacts | null | undefined, now: number = Date.now()): boolean {
    const projectId = studioProjectIdOf(fileUpload);
    if (!projectId) return false;
    try {
        return readProject(projectId, now) !== null;
    } catch {
        return false;
    }
}

/**
 * Take a Studio card back to its project, and say whether it did.
 *
 * `false` means "this is not a Studio workflow, carry on" — the caller then opens the merge
 * re-edit modal it always opened.
 *
 * THROUGH NEXT'S ROUTER, NEVER RAW HISTORY (controller ruling 4). `utils/route-state.ts` owns
 * the SHALLOW, same-page query writes the editor makes and has no cross-route helper: this hop
 * leaves `/downloads` for a tool page, which is a real navigation, and route-state's own KDoc
 * records that every full navigation in this repo is `Router.push`. What ruling 4 rules out is
 * `history.pushState`, whose entries Next's popstate handler skips (`router.js:481-483`) — the
 * spec asserts `pushState` is never called.
 *
 * A throw is swallowed and reported as "not handled": `Router.push` throws
 * `No router instance found` before the router mounts (`client/router.js:118-124`), and the merge
 * modal is a better answer than an exception out of a click handler.
 */
export function openStudioReedit(fileUpload: StudioWorkflowFacts | null | undefined, source?: StudioReeditSource): boolean {
    const href = studioReeditHref(fileUpload, source);
    if (!href) return false;
    try {
        Router.push(href);
        return true;
    } catch {
        return false;
    }
}
